"use client";

import { c as _c } from "react/compiler-runtime";
import { readFragment } from "@opensea/graphql";
import { Lockup, LockupAvatar, LockupContent, LockupTitle } from "@opensea/next-ui-kit";
import { classNames, FlexCenter, LOCKUP_SIZES, TextOverflowTooltip } from "@opensea/ui-kit";
import { createContext, useContext, useMemo } from "react";
import { ChainBadge } from "../ChainBadge";
import { NewCurrencyChip } from "../NewCurrencyChip";
import { CurrencyLockupFragment } from "./CurrencyLockup.fragment";
const CurrencyLockupContext = createContext(null);
const useCurrencyLockup = () => {
  const context = useContext(CurrencyLockupContext);
  if (!context) {
    throw new Error("CurrencyLockup subcomponents must be used within a CurrencyLockup component");
  }
  return context;
};
/**
 * CurrencyLockup - A specialized component for displaying currencies with avatar and name
 *
 * Uses next-ui-kit's Lockup component under the hood for consistent sizing and layout
 */
export function CurrencyLockup(t0) {
  const $ = _c(34);
  let badgeClassName;
  let children;
  let currencyFragment;
  let disableTextOverflowTooltip;
  let lockupProps;
  let overrideBadgeSize;
  let showChainBadge;
  let t1;
  let t2;
  let t3;
  let titleWeight;
  if ($[0] !== t0) {
    ({
      currency: currencyFragment,
      children,
      size: t1,
      showAvatar: t2,
      showChainBadge,
      showNewChip: t3,
      badgeSize: overrideBadgeSize,
      badgeClassName,
      disableTextOverflowTooltip,
      titleWeight,
      ...lockupProps
    } = t0);
    $[0] = t0;
    $[1] = badgeClassName;
    $[2] = children;
    $[3] = currencyFragment;
    $[4] = disableTextOverflowTooltip;
    $[5] = lockupProps;
    $[6] = overrideBadgeSize;
    $[7] = showChainBadge;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = titleWeight;
  } else {
    badgeClassName = $[1];
    children = $[2];
    currencyFragment = $[3];
    disableTextOverflowTooltip = $[4];
    lockupProps = $[5];
    overrideBadgeSize = $[6];
    showChainBadge = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    titleWeight = $[11];
  }
  const size = t1 === undefined ? "md" : t1;
  const showAvatar = t2 === undefined ? true : t2;
  const showNewChip = t3 === undefined ? true : t3;
  let t4;
  if ($[12] !== currencyFragment) {
    t4 = readFragment(CurrencyLockupFragment, currencyFragment);
    $[12] = currencyFragment;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const currency = t4;
  let t5;
  let t6;
  if ($[14] !== currency || $[15] !== size) {
    t6 = {
      currency,
      size
    };
    $[14] = currency;
    $[15] = size;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  t5 = t6;
  const value = t5;
  let t7;
  if ($[17] !== badgeClassName || $[18] !== children || $[19] !== currency || $[20] !== disableTextOverflowTooltip || $[21] !== overrideBadgeSize || $[22] !== showAvatar || $[23] !== showChainBadge || $[24] !== showNewChip || $[25] !== titleWeight) {
    t7 = children ?? <>{showAvatar ? <CurrencyLockupAvatar badge={showChainBadge === false ? undefined : overrideBadgeSize || badgeClassName ? <CurrencyLockupChainBadge className={badgeClassName} size={overrideBadgeSize} /> : undefined} /> : null}<LockupContent><FlexCenter className="gap-1.5"><CurrencyLockupTitle disableTextOverflowTooltip={disableTextOverflowTooltip} weight={titleWeight} /><CurrencyLockupSymbol disableTextOverflowTooltip={disableTextOverflowTooltip} />{showNewChip ? <NewCurrencyChip currency={currency} /> : null}</FlexCenter></LockupContent></>;
    $[17] = badgeClassName;
    $[18] = children;
    $[19] = currency;
    $[20] = disableTextOverflowTooltip;
    $[21] = overrideBadgeSize;
    $[22] = showAvatar;
    $[23] = showChainBadge;
    $[24] = showNewChip;
    $[25] = titleWeight;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== lockupProps || $[28] !== size || $[29] !== t7) {
    t8 = <Lockup size={size} {...lockupProps}>{t7}</Lockup>;
    $[27] = lockupProps;
    $[28] = size;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t8 || $[32] !== value) {
    t9 = <CurrencyLockupContext.Provider value={value}>{t8}</CurrencyLockupContext.Provider>;
    $[31] = t8;
    $[32] = value;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
}
/**
 * CurrencyLockupAvatar - Avatar component for currencies
 *
 * Renders the currency image with optional chain badge.
 * Pass `badge={null}` to hide the chain badge, or pass a custom badge element.
 */
export function CurrencyLockupAvatar(t0) {
  const $ = _c(17);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let avatarProps;
  let badge;
  let className;
  if ($[2] !== t1) {
    ({
      className,
      badge,
      ...avatarProps
    } = t1);
    $[2] = t1;
    $[3] = avatarProps;
    $[4] = badge;
    $[5] = className;
  } else {
    avatarProps = $[3];
    badge = $[4];
    className = $[5];
  }
  const {
    currency,
    size
  } = useCurrencyLockup();
  const badgeSize = LOCKUP_SIZES[size ?? "md"]?.badge;
  const shouldShowDefaultBadge = badge === undefined && currency.chain && badgeSize !== undefined;
  let t2;
  if ($[6] !== badge || $[7] !== badgeSize || $[8] !== shouldShowDefaultBadge) {
    t2 = shouldShowDefaultBadge ? {
      badge: <CurrencyLockupChainBadge size={badgeSize} />
    } : badge && badge !== null ? {
      badge
    } : {};
    $[6] = badge;
    $[7] = badgeSize;
    $[8] = shouldShowDefaultBadge;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const badgeProp = t2;
  const imageSrc = currency.metadata?.fullCollection?.imageUrl ?? currency.imageUrl ?? undefined;
  let t3;
  if ($[10] !== className) {
    t3 = classNames("shrink-0 rounded-full object-cover bg-white", className);
    $[10] = className;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== avatarProps || $[13] !== badgeProp || $[14] !== imageSrc || $[15] !== t3) {
    t4 = <LockupAvatar {...badgeProp} border={true} className={t3} src={imageSrc} {...avatarProps} />;
    $[12] = avatarProps;
    $[13] = badgeProp;
    $[14] = imageSrc;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}
/**
 * CurrencyLockupChainBadge - Chain badge for currency avatars
 */
export function CurrencyLockupChainBadge(t0) {
  const $ = _c(9);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let propSize;
  let rest;
  if ($[2] !== t1) {
    ({
      size: propSize,
      ...rest
    } = t1);
    $[2] = t1;
    $[3] = propSize;
    $[4] = rest;
  } else {
    propSize = $[3];
    rest = $[4];
  }
  const {
    currency,
    size
  } = useCurrencyLockup();
  if (!currency.chain) {
    return null;
  }
  const badgeSize = propSize ?? LOCKUP_SIZES[size ?? "md"]?.badge;
  if (!badgeSize) {
    return null;
  }
  let t2;
  if ($[5] !== badgeSize || $[6] !== currency.chain || $[7] !== rest) {
    t2 = <ChainBadge chain={currency.chain} size={badgeSize} {...rest} />;
    $[5] = badgeSize;
    $[6] = currency.chain;
    $[7] = rest;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}

/**
 * CurrencyLockupContent - Content wrapper for currency name
 *
 * Direct re-export of LockupContent from next-ui-kit
 */
export { LockupContent as CurrencyLockupContent } from "@opensea/next-ui-kit";
export function CurrencyLockupTitle(t0) {
  const $ = _c(18);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let children;
  let className;
  let disableTextOverflowTooltip;
  let titleProps;
  if ($[2] !== t1) {
    ({
      children,
      className,
      disableTextOverflowTooltip,
      ...titleProps
    } = t1);
    $[2] = t1;
    $[3] = children;
    $[4] = className;
    $[5] = disableTextOverflowTooltip;
    $[6] = titleProps;
  } else {
    children = $[3];
    className = $[4];
    disableTextOverflowTooltip = $[5];
    titleProps = $[6];
  }
  const {
    currency
  } = useCurrencyLockup();
  let t2;
  if ($[7] !== className) {
    t2 = classNames("w-auto leading-tight", className);
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = disableTextOverflowTooltip ? undefined : 1;
  let t4;
  if ($[9] !== children || $[10] !== currency.name || $[11] !== titleProps) {
    t4 = children ? children(currency.name) : <LockupTitle weight="semibold" {...titleProps}>{currency.name}</LockupTitle>;
    $[9] = children;
    $[10] = currency.name;
    $[11] = titleProps;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== disableTextOverflowTooltip || $[14] !== t2 || $[15] !== t3 || $[16] !== t4) {
    t5 = <TextOverflowTooltip className={t2} disabled={disableTextOverflowTooltip} lines={t3}>{t4}</TextOverflowTooltip>;
    $[13] = disableTextOverflowTooltip;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
export function CurrencyLockupSymbol(t0) {
  const $ = _c(16);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let className;
  let disableTextOverflowTooltip;
  let symbolProps;
  if ($[2] !== t1) {
    ({
      className,
      disableTextOverflowTooltip,
      ...symbolProps
    } = t1);
    $[2] = t1;
    $[3] = className;
    $[4] = disableTextOverflowTooltip;
    $[5] = symbolProps;
  } else {
    className = $[3];
    disableTextOverflowTooltip = $[4];
    symbolProps = $[5];
  }
  const {
    currency
  } = useCurrencyLockup();
  const t2 = currency.symbol.length <= 8 && "shrink-0";
  let t3;
  if ($[6] !== className || $[7] !== t2) {
    t3 = classNames(t2, className);
    $[6] = className;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== currency.symbol || $[10] !== symbolProps) {
    t4 = <LockupTitle color="text-secondary" weight="regular" {...symbolProps}>{currency.symbol}</LockupTitle>;
    $[9] = currency.symbol;
    $[10] = symbolProps;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== disableTextOverflowTooltip || $[13] !== t3 || $[14] !== t4) {
    t5 = <TextOverflowTooltip className={t3} disabled={disableTextOverflowTooltip}>{t4}</TextOverflowTooltip>;
    $[12] = disableTextOverflowTooltip;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}